import React, { useCallback, useMemo } from 'react'

import { Button } from '@refera/ui-web'

import useRolePermission from '_/hooks/use-role-permission'
import { CanDoButtonAction } from '_/utils/can-do-button-action'
import { BUTTONS_ACTION } from '_/utils/constants/service-order'
import { useToast } from '_/hooks/use-toast'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import { navigate, useLocation, useParams } from '@reach/router'
import { parse } from 'query-string'
import { createBudget, createOpenProposeBudget, updateBudget } from '_/modules/budget/actions'
import { useBudgetForm } from '_/views/budget-view-v2/hooks/use-budget-form'
import { useDispatch } from 'react-redux'
import { useFormContext } from 'react-hook-form'
import { ROUTES } from '_/utils/constants'
import { formatErrorMessage } from '_/views/finance/utils/FormatErrorMessage'
import { verifyIfBudgetIsSent } from '_/utils/verify-if-budget-is-sent'

export default function TemporarySaveButton() {
  const dispatch = useDispatch()
  const { isAdmin, isAdminSAAS, isTradesman, checkIfUserDoesNotPermission, isSAAS } =
    useRolePermission()
  const { showToast } = useToast()
  const { pathname, search } = useLocation()
  const { serviceOrderId, budgetId } = useParams()
  const { tipo: budgetType } = parse(search)

  const { budget, loading, setLoading, isEditing, setIsEditing, fetchBudget } = useBudgetForm()
  const { getValues, handleSubmit } = useFormContext()
  const isBudgetSent = verifyIfBudgetIsSent(budget)
  const isCreating = pathname.includes('/novo')

  const canTemporarySave = CanDoButtonAction({
    nameButton: BUTTONS_ACTION.TEMPORARY_SAVE,
  })

  const canAddInternalBudget = CanDoButtonAction({
    nameButton: BUTTONS_ACTION.ADD_INTERNAL_BUDGET,
  })

  const showButton = useMemo(() => {
    if (isBudgetSent) return false

    const isValidSAASExternalBudgetType =
      budgetType === 'externo' || (budget && budget?.proposeOpened === false)

    const isValidSAASInternalBudgetType =
      budgetType === 'interno' || (budget && budget?.proposeOpened === true)

    const isAdminAndCreatingOrEditing = isAdmin && (isCreating || isEditing)

    const canSaveTemporarily =
      canTemporarySave &&
      (isCreating || (budget && !budget.released && isEditing)) &&
      (isTradesman || (isSAAS && (isValidSAASExternalBudgetType || isValidSAASInternalBudgetType)))

    return (isAdminAndCreatingOrEditing || canSaveTemporarily) && !budget?.budgetParent
  }, [isCreating, isEditing, canTemporarySave, isTradesman, isSAAS, isAdmin, budget, isBudgetSent])

  const handleTemporarySave = useCallback(() => {
    const isSAASCreatingInternalBudget = isSAAS && isCreating && budgetType === 'interno'
    const isSAASCreatingExternalBudget = isSAAS && isCreating && budgetType === 'externo'

    if (
      (!canTemporarySave && !isAdmin) ||
      (isSAASCreatingInternalBudget && !canAddInternalBudget)
    ) {
      showToast({
        message: 'Este chamado está em uma etapa que não permite incluir orçamento.',
        type: 'error',
      })
      return
    }

    // validate by permission
    if (!isAdminSAAS) {
      const unauthorized = checkIfUserDoesNotPermission(
        PERMISSIONS.ORIGINAL_BUDGETS,
        PERMISSIONS_ACTIONS.ADD
      )

      // if unauthorized, shows toast and return
      if (unauthorized) {
        return
      }
    }

    const payload = getValues()
    payload.visitHappenedTemp = String(payload.visitHappenedTemp)

    if (!budget) {
      // creating budget
      const params = { edit: true }
      if (budgetType) params.tipo = budgetType

      if (isSAASCreatingExternalBudget || isTradesman) {
        payload.serviceOrder = serviceOrderId

        setLoading(true)
        dispatch(createBudget(payload))
          .then(newBudget => {
            showToast({ type: 'success' })
            const url =
              `${ROUTES.SERVICE_ORDERS}/${serviceOrderId}/orcamentos/${newBudget?.id}` +
              `?${new URLSearchParams(params).toString()}`
            navigate(url)
          })
          .catch(error => showToast({ type: 'error', message: formatErrorMessage(error) }))
          .finally(() => setLoading(false))
      }
      if (isSAASCreatingInternalBudget || isAdmin) {
        setLoading(true)
        dispatch(createOpenProposeBudget(serviceOrderId, payload))
          .then(newBudgetId => {
            showToast({ type: 'success' })
            const url =
              `${ROUTES.SERVICE_ORDERS}/${serviceOrderId}/orcamentos/${newBudgetId}` +
              `?${new URLSearchParams(params).toString()}`
            navigate(url)
          })
          .catch(error =>
            showToast({
              message: formatErrorMessage(error),
              type: 'error',
            })
          )
          .finally(() => setLoading(false))
      }
    } else if (budget) {
      // already has budget
      // removing fields that are not needed in the payload
      const { budgetResolutions, ...payloadToSend } = payload
      payloadToSend.newBudgetResolutionsOrder = budgetResolutions.map(({ id }) => id).join(',')
      payloadToSend.visitHappenedTemp = String(payloadToSend.visitHappenedTemp)

      setLoading(true)
      const options = { transformPayload: true, transformFormData: true, formData: true }
      dispatch(updateBudget(serviceOrderId, budgetId, payloadToSend, options))
        .then(() => {
          fetchBudget(budgetId)
          showToast({ type: 'success' })
          setIsEditing(false)
        })
        .catch(error =>
          showToast({
            message: formatErrorMessage(error),
            type: 'error',
          })
        )
        .finally(() => setLoading(false))
    }
  }, [
    canTemporarySave,
    isAdminSAAS,
    getValues,
    serviceOrderId,
    budgetId,
    dispatch,
    setLoading,
    showToast,
    budget,
    isAdmin,
    fetchBudget,
    isSAAS,
    isTradesman,
    isCreating,
    budgetType,
    canAddInternalBudget,
  ])

  if (!showButton) return null

  return (
    <Button
      onClick={handleSubmit(handleTemporarySave)}
      color="primary"
      variant="secondary"
      disabled={loading}
    >
      Salvar temporário
    </Button>
  )
}
